import React, { useState, useEffect } from 'react';
import { ConfigProvider, useConfig } from './context/ConfigContext';
import { EmployerProvider, useEmployer } from './context/EmployerContext';
import { ClientsProvider } from './context/ClientsContext';
import { UsersProvider, useUsers } from './context/UsersContext';
import { ToastProvider } from './components/Toast';
import Login from './components/Login';
import EmployerPortal from './components/EmployerPortal';
import EmployeeDashboard from './components/employee/EmployeeDashboard';
import IndividualView from './components/IndividualView';
import BulkView from './components/BulkView';
import SpreadView from './components/SpreadView';
import AdminPanel from './components/admin/AdminPanel';
import ClientsView from './components/ClientsView';
import InternalDashboard from './components/dashboard/InternalDashboard';
import EmployerSelector from './components/EmployerSelector';
import HealthCuesLogo from './components/HealthCuesLogo';
import UserProfileDropdown from './components/UserProfileDropdown';
import ProfileModal from './components/ProfileModal';
import ChangePasswordModal from './components/ChangePasswordModal';
import TaxSyncNotificationBanner from './components/TaxSyncNotificationBanner';
import MyTrainingHome from './components/learner/MyTrainingHome';
import PublicVerifyCertificatePage from './components/learner/PublicVerifyCertificatePage';
import { getGatingStatus } from './components/learner/learnerApi';
import useUnreadMessages from './hooks/useUnreadMessages';
import useIsMobile from './hooks/useIsMobile';
import MobileNavDrawer from './components/MobileNavDrawer';
import { C } from './utils/styles';
import './App.css';

function MainApp() {
  const { currentUser, logout, isSuperUser } = useUsers();
  const { loadEmployer } = useEmployer();
  const [tab, setTab] = useState('dashboard');
  const [salary, setSalary] = useState(31200);
  const [state, setState] = useState('CA');
  const [filing, setFiling] = useState('single');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [pendingClientToOpen, setPendingClientToOpen] = useState(null);
  const [gating, setGating] = useState(null);
  // Unread message counts for the Clients tab badge (must be before any early returns to obey hooks rules)
  const isInternalUser = currentUser && currentUser.role !== 'employer_contact' && currentUser.role !== 'employee';
  const { total: unreadTotal } = useUnreadMessages(!!isInternalUser);
  const isMobile = useIsMobile(720);

  // Fetch gating status for the AM hard-block. Only relevant for internal (non-super) users
  // but we call it unconditionally for authenticated users so the response can also feed
  // the Employer / Employee portals via the soft banner.
  useEffect(() => {
    if (!currentUser) { setGating(null); return; }
    let mounted = true;
    getGatingStatus()
      .then((d) => { if (mounted) setGating(d); })
      .catch(() => { if (mounted) setGating({ blocked: false, isHardBlocked: false, incompleteMandatory: [] }); });
    return () => { mounted = false; };
  }, [currentUser]);

  // Detect the public /verify-certificate/{id} route — works without auth
  if (typeof window !== 'undefined') {
    const path = window.location.pathname || '';
    const verifyMatch = path.match(/^\/verify-certificate\/([^/?#]+)/);
    if (verifyMatch) {
      return <PublicVerifyCertificatePage certificateId={decodeURIComponent(verifyMatch[1])} />;
    }
  }

  if (!currentUser) {
    return <Login />;
  }
  
  // Route employer contacts to their portal
  if (currentUser.role === 'employer_contact') {
    return <EmployerPortal gatingStatus={gating} />;
  }

  // Route employees to their dashboard
  if (currentUser.role === 'employee') {
    return <EmployeeDashboard gatingStatus={gating} />;
  }

  // AM hard-block: if there are uncomplete mandatory courses, force them into My Training and grey-out other tabs.
  const isHardBlocked = !!(gating && gating.isHardBlocked);

  // Define visible tabs based on role (My Training is always visible to internal users)
  const baseTabs = isSuperUser() 
    ? ['dashboard', 'individual', 'bulk', 'spread', 'clients', 'myTraining', 'admin']
    : ['dashboard', 'individual', 'bulk', 'spread', 'clients', 'myTraining'];
  const visibleTabs = baseTabs;

  // Force the active tab to myTraining when AM is hard-blocked
  if (isHardBlocked && tab !== 'myTraining') {
    setTab('myTraining');
  }

  // Redirect if trying to access admin as non-super user
  if (tab === 'admin' && !isSuperUser()) {
    setTab('dashboard');
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", padding: isMobile ? '14px 14px 20px' : '20px', overflowX: isMobile ? 'hidden' : 'visible', maxWidth: '100vw' }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {(() => {
        const allTabs = [
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'individual', label: 'Individual' },
          { id: 'bulk', label: 'Workforce' },
          { id: 'spread', label: 'Economics' },
          { id: 'clients', label: 'Clients' },
          { id: 'myTraining', label: 'My Training' },
          { id: 'admin', label: 'Admin' },
        ];
        const navTabs = allTabs.filter(t => visibleTabs.includes(t.id));
        return (
      <div style={{ maxWidth: 1300, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: isMobile ? 10 : 16, flexWrap: 'nowrap' }}>
          {/* LEFT: Branding — full title on desktop, compact wordmark on mobile */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '0 1 auto' }}>
            <HealthCuesLogo size={isMobile ? 28 : 34} />
            <div style={{ minWidth: 0 }}>
              {isMobile ? (
                <h1 style={{ fontSize: 17, fontWeight: 800, margin: 0, letterSpacing: '-0.02em', whiteSpace: 'nowrap', color: C.text }}>HealthCues</h1>
              ) : (
                <>
                  <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>HealthCues Client Success Engine</h1>
                  <p style={{ color: C.textLight, fontSize: 12, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    6 plan tiers · 50 states + DC · Bulk workforce analysis · Spread-based revenue model
                  </p>
                </>
              )}
            </div>
          </div>

          {/* RIGHT: Navigation Tabs (desktop) OR Burger (mobile) + Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, flexShrink: 0 }}>
            {!isMobile && (
              <div style={{ display: 'flex', gap: 3 }}>
                {navTabs.map((t) => {
                  const showBadge = t.id === 'clients' && unreadTotal > 0;
                  const tabDisabled = isHardBlocked && t.id !== 'myTraining';
                  return (
                    <button
                      key={t.id}
                      data-testid={`tab-${t.id}`}
                      onClick={() => !tabDisabled && setTab(t.id)}
                      disabled={tabDisabled}
                      title={tabDisabled ? 'Complete required training to unlock' : undefined}
                      style={{
                        padding: '8px 18px',
                        borderRadius: 8,
                        border: 'none',
                        background: tab === t.id ? C.accent : 'transparent',
                        color: tab === t.id ? '#fff' : (tabDisabled ? C.textLight : C.textMid),
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: tabDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s',
                        whiteSpace: 'nowrap',
                        position: 'relative',
                        opacity: tabDisabled ? 0.4 : 1,
                        WebkitTapHighlightColor: 'rgba(15, 23, 42, 0.08)',
                      }}
                    >
                      {t.label}
                      {showBadge && (
                        <span
                          data-testid="clients-unread-badge"
                          style={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            minWidth: 18,
                            height: 18,
                            padding: '0 5px',
                            borderRadius: 9,
                            background: '#dc2626',
                            color: '#fff',
                            fontSize: 10,
                            fontWeight: 800,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                            border: '2px solid #fff',
                          }}
                        >
                          {unreadTotal > 9 ? '9+' : unreadTotal}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {isMobile && (
              <button
                type="button"
                data-testid="mobile-nav-burger"
                aria-label="Open navigation menu"
                aria-expanded={mobileNavOpen}
                onClick={() => setMobileNavOpen(true)}
                style={{
                  position: 'relative',
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  WebkitTapHighlightColor: 'rgba(15, 23, 42, 0.08)',
                  // Removes the 300ms tap delay on iOS Safari and makes
                  // the button feel instantly responsive on touch.
                  touchAction: 'manipulation',
                  flexShrink: 0,
                  // Ensure nothing in the document tree can sit on top of
                  // the burger and swallow the tap (the drawer aside has
                  // z-index 1001 but is pointer-events:none when closed).
                  zIndex: 10,
                }}
              >
                <span aria-hidden="true" style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, pointerEvents: 'none' }}>
                  <span style={{ width: 18, height: 2, background: C.text, borderRadius: 2, pointerEvents: 'none' }} />
                  <span style={{ width: 18, height: 2, background: C.text, borderRadius: 2, pointerEvents: 'none' }} />
                  <span style={{ width: 18, height: 2, background: C.text, borderRadius: 2, pointerEvents: 'none' }} />
                </span>
                {unreadTotal > 0 && (
                  <span
                    data-testid="burger-unread-badge"
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      minWidth: 18,
                      height: 18,
                      padding: '0 5px',
                      borderRadius: 9,
                      background: '#dc2626',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #fff',
                    }}
                  >
                    {unreadTotal > 9 ? '9+' : unreadTotal}
                  </span>
                )}
              </button>
            )}

            <UserProfileDropdown
              onOpenProfile={() => setShowProfileModal(true)}
              onOpenChangePassword={() => setShowPasswordModal(true)}
            />
          </div>
        </div>

        {/* Mobile slide-in drawer */}
        {isMobile && (
          <MobileNavDrawer
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            tabs={navTabs}
            activeTab={tab}
            onSelect={(id) => setTab(id)}
            isHardBlocked={isHardBlocked}
            unreadTotal={unreadTotal}
            currentUser={currentUser}
          />
        )}

        {/* Hard-block banner for AMs with required incomplete training */}
        {isHardBlocked && (
          <div data-testid="hard-block-banner" style={{ marginBottom: 16, padding: '14px 18px', background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', border: `1px solid ${C.red}33`, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ width: 36, height: 36, borderRadius: '50%', background: C.red, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800 }}>!</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#7f1d1d' }}>Required training pending</div>
              <div style={{ fontSize: 12, color: '#991b1b', marginTop: 2 }}>
                Portal access is locked until you complete {(gating?.incompleteMandatory || []).length} mandatory course
                {(gating?.incompleteMandatory || []).length === 1 ? '' : 's'}: <strong>{(gating?.incompleteMandatory || []).map((c) => c.title).slice(0, 3).join(' · ')}</strong>
              </div>
            </div>
          </div>
        )}

        {/* Employer Context Bar — full-width on its own row so it never pushes the nav.
            Hidden on Dashboard / My Training since they're portfolio-wide / personal. */}
        {tab !== 'dashboard' && tab !== 'myTraining' && (
          <div style={{ marginBottom: 20 }}>
            <EmployerSelector />
          </div>
        )}

        {/* Tax Sync Notifications (Super Users only) */}
        {isSuperUser() && <TaxSyncNotificationBanner />}

        {/* Content */}
        {tab === 'dashboard' && (
          <InternalDashboard
            onOpenClient={(clientId) => {
              setPendingClientToOpen(clientId);
              setTab('clients');
            }}
            onSwitchTab={(t) => setTab(t)}
          />
        )}
        {tab === 'individual' && <IndividualView salary={salary} setSalary={setSalary} state={state} setState={setState} filing={filing} setFiling={setFiling} />}
        {tab === 'bulk' && <BulkView />}
        {tab === 'spread' && <SpreadView salary={salary} state={state} filing={filing} />}
        {tab === 'clients' && <ClientsView
          autoOpenClientId={pendingClientToOpen}
          onAutoOpenComplete={() => setPendingClientToOpen(null)}
          onOpenAnalysis={(clientId) => {
            setTab('bulk');
          }}
        />}
        {tab === 'myTraining' && <MyTrainingHome />}
        {tab === 'admin' && <AdminPanel />}

        <div style={{ marginTop: 20, fontSize: 10, color: C.textLight, textAlign: 'center', lineHeight: 1.5 }}>
          *This is an optimization tool using estimated tax data. HealthCues cannot guarantee the same favorable tax outcome in all situations as federal and state tax laws are continuously changing. This program does not replace major medical insurance. Consult a professional tax advisor.
        </div>
      </div>
        );
      })()}

      {/* Modals */}
      {showProfileModal && (
        <ProfileModal onClose={() => setShowProfileModal(false)} />
      )}
      
      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
}

function AppWithConfig() {
  const { config } = useConfig();
  
  return (
    <ClientsProvider config={config}>
      <EmployerProvider config={config}>
        <MainApp />
      </EmployerProvider>
    </ClientsProvider>
  );
}

export default function App() {
  return (
    <ConfigProvider>
      <UsersProvider>
        <ToastProvider>
          <AppWithConfig />
        </ToastProvider>
      </UsersProvider>
    </ConfigProvider>
  );
}
